import { GtmhubController } from "@gtmhub/core";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { IIndicator, UIErrorHandlingService } from "@gtmhub/error-handling";
import { hasFiltersGroupsWithValues } from "@gtmhub/goals/filter-utils";
import { localize } from "@gtmhub/localization";
import { IGtmhubRootScopeService } from "@gtmhub/models";
import { WarningModalService } from "@gtmhub/shared/components";
import {
  getPersistedScreenSettings,
  GridColumnDefinitions,
  GridFilterDefinitions,
  GridSortingDefinitions,
  IGridColumnDefinition,
  IGridFiltersChange,
  IGridSortingDefinition,
  IGridSortingRule,
  OrFiltersGroup,
  setPersistedScreenSettings,
  toGridDefinitionMap,
  toRestLayerRequest,
} from "@gtmhub/shared/components/grid-filtering-bar";
import { IGHCellChangePayload, IGHGridColumn } from "@gtmhub/shared/components/grid/grid.component";
import { ITag, ITagChangeStatusAction } from "@gtmhub/tags/models";
import { TagService } from "@gtmhub/tags/tagService";
import { isDuplicateTagTitleResponseError } from "@gtmhub/tags/utils";
import { StateService } from "@uirouter/core";
import { IComponentOptions, IPromise, IQService } from "angular";
import { getDefaultFilters, getDefaultSortingRules, getTagsGridColumns, getTagsGridFilterDefinitions } from "./tags-grid.config";

const SCREEN_PATH = ["tags"];
const COLUMN_READ_ONLY_FIELD_NAMES = ["title"];
const FIELD_NAME_ACTIONS = "actions";
const DEFAULT_PAGING_SIZE = 40;
const FIRST_PAGE = 1;

export interface ITagsGridStateParams {
  page?: string;
}

export type ITagsGridChangeAction = ITagChangeStatusAction | "rename" | "delete";

export interface ITagsGridChangePayload extends IGHCellChangePayload<ITag> {
  action: ITagsGridChangeAction;
  rowIndex: number;
}

export interface ITagsGridComponentBindings {
  pagingSize?: number;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface TagsGridCtrl extends ITagsGridComponentBindings {}
export class TagsGridCtrl extends GtmhubController {
  public static $inject = ["$rootScope", "$state", "$stateParams", "TagService", "WarningModalService", "UIErrorHandlingService", "$q"];

  public initialized = false;
  public totalPages: number;
  public currentPage: number;

  public indicators: {
    loading?: IIndicator;
  } = {};
  public tags: ITag[];
  public gridColumns: IGHGridColumn[];
  public columns: IGHGridColumn[];
  public columnDefinitions: GridColumnDefinitions;
  public filters: OrFiltersGroup[];
  public filterDefinitions: GridFilterDefinitions;
  public sortingRules: IGridSortingRule[];
  public sortingDefinitions: GridSortingDefinitions;
  public showEmptyScreen: boolean;
  public showNoFilterResults: boolean;
  private loadingRowsIndexMap: { [key: string]: boolean } = {};

  get hasPermission(): boolean {
    return this.$rootScope.hasPermission("ManageConfiguration") && this.$rootScope.hasPermission("CreateTags");
  }

  constructor(
    private $rootScope: IGtmhubRootScopeService & ITraceRootScopeService,
    private $state: StateService,
    private $statePrams: ITagsGridStateParams,
    private tagService: TagService,
    private warningModalService: WarningModalService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private $q: IQService
  ) {
    super();
  }

  public $onInit(): IPromise<void> {
    if (!this.hasPermission) {
      return;
    }

    this.$rootScope.$on("tagsChanged", () => this.getTags());

    this.filterDefinitions = toGridDefinitionMap(getTagsGridFilterDefinitions());
    this.columnDefinitions = {};
    this.sortingDefinitions = {};
    const tagsGridColumns = getTagsGridColumns();
    for (const column of tagsGridColumns) {
      if (column.fieldName === FIELD_NAME_ACTIONS) {
        continue;
      }
      const definition: IGridColumnDefinition | IGridSortingDefinition = {
        displayName: column.label,
        fieldName: column.fieldName,
        trackingName: column.label,
        readOnly: COLUMN_READ_ONLY_FIELD_NAMES.includes(column.fieldName),
      };
      this.columnDefinitions[column.fieldName] = { ...definition };
      this.sortingDefinitions[column.fieldName] = { ...definition };
    }
    this.filters = getPersistedScreenSettings(SCREEN_PATH, "filters") || [getDefaultFilters()];
    this.columns = getPersistedScreenSettings(SCREEN_PATH, "tagsGridColumns") || tagsGridColumns.slice();
    this.gridColumns = this.columns.filter((column: IGHGridColumn): boolean => column.visible);
    this.sortingRules = getPersistedScreenSettings(SCREEN_PATH, "sorting") || getDefaultSortingRules();

    this.currentPage = Number(this.$statePrams.page) || FIRST_PAGE;
    this.pagingSize = this.pagingSize || DEFAULT_PAGING_SIZE;
  }

  public onColumnsChange(): void {
    this.$rootScope.traceAction("change_tags_grid_columns", () => {
      const columns: IGHGridColumn[] = [];
      for (const { fieldName } of [...this.gridColumns, { fieldName: FIELD_NAME_ACTIONS }]) {
        const column = getTagsGridColumns().find((c) => c.fieldName === fieldName);
        if (!column) {
          continue;
        }
        columns.push({ ...column });
      }
      this.columns = columns;
      setPersistedScreenSettings(SCREEN_PATH, "tagsGridColumns", this.columns);
    });
  }

  public onSortingChange(): void {
    this.$rootScope.traceAction("change_tags_grid_sorting", () => {
      setPersistedScreenSettings(SCREEN_PATH, "sorting", this.sortingRules);
      this.currentPage === FIRST_PAGE ? this.getTags() : this.onPageChange(FIRST_PAGE);
    });
  }

  public onFiltersChange(change: IGridFiltersChange): void {
    this.$rootScope.traceAction("change_tags_grid_filters", () => {
      if (change.initial) {
        return this.getTags().then(() => {
          this.initialized = true;
        });
      }
      setPersistedScreenSettings(SCREEN_PATH, "filters", this.filters);
      this.currentPage === FIRST_PAGE ? this.getTags() : this.onPageChange(FIRST_PAGE);
    });
  }

  public onGridChange(payload: ITagsGridChangePayload): IPromise<void | unknown> {
    const { item, action, rowIndex } = payload;

    if (!item || !action || rowIndex === undefined) {
      setPersistedScreenSettings(SCREEN_PATH, "tagsGridColumns", this.columns);
      return;
    }

    this.loadingRowsIndexMap[rowIndex] = true;
    switch (action) {
      case "activate":
      case "deactivate":
        return this.changeTagActivationStatus(item, action).then(() => {
          delete this.loadingRowsIndexMap[rowIndex];
        });
      case "delete":
        return this.warningModalService
          .warningModal({
            modalImage: "/dist/img/cannot-continue.svg",
            modalTitle: "delete_tag",
            modalText: localize("tag_is_being_used_in_warning", { usedIn: item.itemsTaggedCount }),
            confirmBtnType: "destructive",
            confirmText: "delete",
            dismissText: "cancel",
          })
          .then(() => {
            this.deleteTag(item).then(() => {
              delete this.loadingRowsIndexMap[rowIndex];
            });
          })
          .catch(() => {
            delete this.loadingRowsIndexMap[rowIndex];
          });
      case "rename":
        return this.tagService
          .renameTag(item.id, item.title)
          .catch((error) => {
            if (isDuplicateTagTitleResponseError(error)) {
              return this.warningModalService
                .warningModal({
                  modalTitle: "merge_tags",
                  modalText: "tag_exists_merge_warning",
                  modalImage: "/dist/img/cannot-continue.svg",
                  modalClass: "merge-tags-confirm-modal",
                  confirmBtnType: "destructive",
                  confirmText: "merge",
                  dismissText: "cancel",
                })
                .then(() => this.mergeTags([item.id], item.title))
                .catch(() => error);
            }

            this.uiErrorHandlingService.handleModal(error);
            return error;
          })
          .finally(() => {
            delete this.loadingRowsIndexMap[rowIndex];
          });
      default:
        delete this.loadingRowsIndexMap[rowIndex];
    }
  }

  public onPageChange(page: number): void {
    this.$state.go(".", { page }, { notify: false });
  }

  public resetIndicator(indicator: keyof TagsGridCtrl["indicators"]): void {
    delete this.indicators[indicator];
  }

  public isRowLoading(index: number): boolean {
    return Boolean(this.loadingRowsIndexMap[index]);
  }

  private getTags(): IPromise<void> {
    this.indicators.loading = { progress: true };

    const req = toRestLayerRequest({ filters: this.filters, sorting: this.sortingRules });
    req.skip = (this.currentPage - 1) * this.pagingSize;
    req.limit = this.pagingSize;
    return this.tagService.getTagsV2(req).then(
      (res) => {
        this.totalPages = Math.ceil(res.totalCount / this.pagingSize);
        this.tags = res.items;
        this.resetIndicator("loading");
        this.setEmptyScreenAndNoFilterDisplay();
      },
      (error) => {
        this.indicators.loading = { error };
      }
    );
  }

  private changeTagActivationStatus(tag: ITag, action: ITagChangeStatusAction): IPromise<void> {
    return this.tagService.changeTagsActivationStatus([tag.id], action).then(
      () => {
        tag.isActive = action === "activate";
      },
      (error) => {
        this.indicators.loading = { error };
      }
    );
  }

  private deleteTag(tag: ITag): IPromise<void> {
    this.indicators.loading = { progress: true };
    return this.tagService
      .deleteTags([tag.id])
      .then(
        () => {
          // `tagsChanged` $rootScope listener will trigger `getTags()` to synchronize
          // local state with backend. However the tag that has just been deleted
          // is removed from the `tags` array so that deleted item is not displayed
          // after modal window is closed.
          this.tags = this.tags.filter((t) => t.id !== tag.id);
        },
        (error) => {
          this.uiErrorHandlingService.handleModal(error);
        }
      )
      .finally(() => {
        this.resetIndicator("loading");
      });
  }

  private mergeTags(mergeFromIds: string[], mergeToName: string): IPromise<void> {
    return this.tagService.mergeTags(mergeFromIds, mergeToName).catch((error) => {
      this.uiErrorHandlingService.handleModal(error);
      return this.$q.reject(error);
    });
  }

  private setEmptyScreenAndNoFilterDisplay = () => {
    const noTagsFetched = !this.indicators.loading && this.tags.length === 0;
    const hasAnyFilterWithValue = hasFiltersGroupsWithValues(this.filters);
    this.showEmptyScreen = noTagsFetched && !hasAnyFilterWithValue;
    this.showNoFilterResults = noTagsFetched && hasAnyFilterWithValue;
  };
}

export const TagsGridComponent: IComponentOptions = {
  controller: TagsGridCtrl,
  templateUrl: require("./tags-grid.component.jade"),
  bindings: {
    pagingSize: "<?",
  },
};
